import { useEffect } from 'react';

export interface ShortcutDef {
  key: string;
  meta?: boolean;
  shift?: boolean;
  handler: () => void;
  skipInInput?: boolean;
}

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export default function useKeyboardShortcuts(shortcuts: ShortcutDef[]) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // `e.key` can be undefined during IME composition, autofill events, or
      // synthetic events dispatched by some MUI dialogs. Bail early — these
      // aren't real user keystrokes we'd match a shortcut to.
      if (!e.key) return;

      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const metaKey = isMac ? e.metaKey : e.ctrlKey;

      for (const s of shortcuts) {
        if (!s.key) continue;
        const keyMatch = e.key.toLowerCase() === s.key.toLowerCase();
        const metaMatch = s.meta ? metaKey : !metaKey;
        const shiftMatch = s.shift ? e.shiftKey : !e.shiftKey;

        if (!keyMatch || !metaMatch || !shiftMatch) continue;

        const el = document.activeElement;
        const isInInput = el && INPUT_TAGS.has(el.tagName);
        const isInDialog = el && el.closest('[role="dialog"]');

        if (s.skipInInput && isInInput) continue;
        if (s.key === 'Escape' && (isInInput || isInDialog)) continue;

        e.preventDefault();
        s.handler();
        return;
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [shortcuts]);
}
